import { useJsonPermission } from "lib/hooks/useJsonPermission";
import { BannerType } from "lib/types/checkout";
import { BannerConfig, BannerItem } from "lib/types/merchantConfigTypes";
import { hexToRgba } from "lib/utils/helpers";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { Warning } from "@phosphor-icons/react";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";

interface CartBannerProps {
  bannerPosition: BannerType;
  bannerConfig?: BannerConfig;
}

const CartBannerStrip: React.FC<CartBannerProps> = ({ bannerPosition = BannerType.HEADER, bannerConfig }) => {
  const {
    state: { cartMetadata },
  } = useMerchantContext();

  const {
    state: { checkoutModal },
  } = useCheckoutContext();

  const { cartPermissions } = useJsonPermission();

  const getBorderColor = useCallback((backgroundColor: string) => {
    return hexToRgba(backgroundColor.substring(0, 7) ?? "#000000", 0.5);
  }, []);

  const isMultiple = !!(bannerConfig?.multipleBannersEnabled && bannerConfig.banners?.length);
  const banners: BannerItem[] = isMultiple ? bannerConfig!.banners ?? [] : [];
  const animationDelay = bannerConfig?.animationDelay ?? 1;
  const isDiscountModalOpen = checkoutModal ? checkoutModal === "DISCOUNT_LIST" : false;

  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!isMultiple || banners.length <= 1) return;
    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % banners.length);
    }, animationDelay * 1000);
    return () => clearInterval(id);
  }, [isMultiple, banners.length, animationDelay]);

  const getStyle = useCallback(
    (bgColor: string, textColor: string) => {
      const borderColor = getBorderColor(bgColor);
      if (bannerPosition === BannerType.HEADER) {
        return {
          backgroundColor: bgColor,
          color: textColor,
          boxShadow: "0px 1px 5px 0px rgba(0, 0, 0, 0.2)",
          borderBottom: `1px solid ${borderColor}`,
        };
      }
      return {
        backgroundColor: bgColor,
        color: textColor,
        boxShadow: "0px -1px 5px 0px rgba(0, 0, 0, 0.2)",
      };
    },
    [bannerPosition, getBorderColor],
  );

  const baseClassName =
    bannerPosition === BannerType.HEADER
      ? "flex min-h-[26px] w-full items-center justify-center px-3 py-1 text-center text-text-xs-semibold"
      : "flex min-h-5 w-full items-center justify-center px-3 text-center text-text-xxs-bold";

  if (cartMetadata?.metadataLoading) {
    return <div className="skeleton-loader sticky top-0 z-20 flex min-h-[1.875rem] w-full"></div>;
  }

  const renderContent = () => {
    if (!bannerConfig) return null;

    if (isMultiple && !isDiscountModalOpen) {
      const active = banners[activeIndex];

      if (bannerConfig.animationStyle === "horizontal_marquee") {
        return (
          <div className={baseClassName} style={getStyle(active.backgroundColor, active.textColor)}>
            <div className="overflow-hidden w-full flex justify-center">
              <AnimatePresence mode="popLayout" initial={false}>
                <motion.div
                  key={activeIndex}
                  className="min-w-0 max-w-full truncate text-center"
                  initial={{ x: "100%", opacity: 0, filter: "blur(8px)" }}
                  animate={{ x: 0, opacity: 1, filter: "blur(0px)" }}
                  exit={{ x: "-100%", opacity: 0, filter: "blur(8px)" }}
                  transition={{ type: "spring", duration: 0.6, bounce: 0.1 }}
                  dangerouslySetInnerHTML={{ __html: active.htmlTextString }}
                />
              </AnimatePresence>
            </div>
          </div>
        );
      }

      // vertical_ticker (default)
      return (
        <div className={baseClassName} style={getStyle(active.backgroundColor, active.textColor)}>
          <div className="overflow-hidden w-full flex justify-center">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.div
                key={activeIndex}
                className="min-w-0 max-w-full truncate text-center"
                initial={{ y: "-100%", opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: "100%", opacity: 0 }}
                transition={{ type: "spring", duration: 0.5, bounce: 0.1 }}
                dangerouslySetInnerHTML={{ __html: active.htmlTextString }}
              />
            </AnimatePresence>
          </div>
        </div>
      );
    }

    return (
      <div
        className={baseClassName}
        style={getStyle(bannerConfig.backgroundColor, bannerConfig.textColor)}
        dangerouslySetInnerHTML={{ __html: bannerConfig.htmlTextString }}
      />
    );
  };

  return (
    <div className="sticky top-0 z-20 isolate">
      {bannerConfig?.enabled && renderContent()}
      {bannerPosition === BannerType.FOOTER && cartPermissions?.checkoutBtnStatus && (
        <div className="flex items-start gap-2 min-h-[33px] w-full justify-center px-3 py-2 text-center text-sm bg-coal-dark">
          <div>
            <Warning className="h-4 w-4 text-yellow-light" />
          </div>
          <span className="text-white">{cartPermissions?.checkoutBtnStatus}</span>
        </div>
      )}
    </div>
  );
};

export default CartBannerStrip;
